import React from "react";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";

export interface U2000CheckBoxProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  readonly?: boolean;
  /** BL-0388 : éditable même si le formulaire parent est en lecture. */
  ignoreFormReadState?: boolean;
  /** Affiche la checkbox en état d’erreur (bordure rouge) */
  error?: boolean;
}

// Utilitaire pour accéder à une propriété imbriquée d'un objet à partir d'un chemin (ex: "user.name")
function getNestedValue<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

const U2000CheckBox: React.FC<U2000CheckBoxProps> = ({
  formStateHandlers,
  fieldName,
  label,
  readonly = false,
  ignoreFormReadState = false,
  error = false,
}) => {
  function handleCheck(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (formStateHandlers.onChange) {
      formStateHandlers.onChange(e, checked);
    }
  }

  // Determine disabled state using readonly and, if available, the form state's editMode.
  const disabled =
    readonly || (!ignoreFormReadState && formStateHandlers.getFormState() === U2000FormStateEnumState.READ);

  const checked = Boolean(getNestedValue(formStateHandlers, fieldName) || false);

  return (
    <div className="flex h-full items-center gap-3">
      <input
        id={fieldName}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => !disabled && handleCheck(e, e.target.checked)}
        className="peer absolute opacity-0 h-5 w-5 cursor-pointer"
        aria-checked={checked}
        aria-disabled={disabled}
      />
      <label
        htmlFor={fieldName}
        className={`relative flex h-5 w-5 items-center justify-center rounded border transition-all duration-200 cursor-pointer
          ${
            error
              ? "border-red-500 bg-red-50 hover:border-red-600"
              : disabled
                ? "cursor-not-allowed surface-border bg-muted"
                : checked
                  ? "border-blue-500 bg-blue-500 hover:bg-blue-600 hover:border-blue-600"
                  : "border-gray-300 bg-white hover:border-gray-400 hover:bg-muted"
          }
          peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/20 peer-focus-visible:ring-offset-2
        `}
      >
        {checked && (
          <svg className={disabled ? "text-gray-400" : "text-white"} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="14" height="14">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </label>
      <label
        htmlFor={fieldName}
        className={`flex h-full items-center text-sm font-medium cursor-pointer leading-relaxed ${disabled ? "cursor-not-allowed text-gray-500" : "text-gray-900 hover:text-gray-700"}`}
      >
        {label}
      </label>
    </div>
  );
};

export default U2000CheckBox;
